/**
 * LocationPagesPanel — "Areas We Serve" generator
 *
 * Flow:
 *   1. User enters service area names (tag-style multi-entry)
 *   2. Optionally provides comma-separated services
 *   3. Clicks "Generate Pages" → preview rendered via srcdoc iframes
 *   4. Similarity report shown (anti-doorway guardrail visibility)
 *   5. Clicks "Add to Site" → committed to site document
 *
 * Styling: GlassCard container, GlassButton (primary CTAs, secondary Cancel),
 * inline styles + CSS variables only — no new CSS file needed.
 * Error banners match GenerationWizard.tsx pattern (#ef4444/#fef2f2).
 */

import React, { useState, useRef, KeyboardEvent } from 'react';
import { X, MapPin, Plus, Loader, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import {
  generateLocationPages,
  commitLocationPages,
  GenerateLocationPagesPayload,
  SimilarityReportEntry,
} from '../../services/api';

interface Props {
  siteId: string;
  onCommitted: () => void;  // parent re-fetches site after commit
  onClose: () => void;
}

interface AreaEntry {
  id: string;
  area_name: string;
  region: string;
}

interface PreviewPage {
  page_id: string;
  title: string;
  slug: string;
  preview_html: string;
  page_role?: string;
  location_meta?: { area_name: string; region?: string; slug_base?: string };
  [key: string]: any;
}

const LocationPagesPanel: React.FC<Props> = ({ siteId, onCommitted, onClose }) => {
  // --- Input state ---
  const [areas, setAreas] = useState<AreaEntry[]>([]);
  const [areaInput, setAreaInput] = useState('');
  const [servicesText, setServicesText] = useState('');
  const [includeTestimonials, setIncludeTestimonials] = useState(true);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.45);

  // --- Generation state ---
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [previews, setPreviews] = useState<PreviewPage[]>([]);
  const [similarityReport, setSimilarityReport] = useState<SimilarityReportEntry[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  // --- Preview UI ---
  const [expandedPreviews, setExpandedPreviews] = useState<Set<string>>(new Set());

  // --- Commit state ---
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState('');
  const [committed, setCommitted] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Area tag management
  // ---------------------------------------------------------------------------

  const addArea = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    // Parse "City, Region" or just "City"
    const parts = trimmed.split(',').map(p => p.trim());
    const area_name = parts[0] || '';
    const region = parts[1] || '';
    if (!area_name) return;
    setAreas(prev => [...prev, { id: crypto.randomUUID(), area_name, region }]);
    setAreaInput('');
  };

  const handleAreaKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addArea(areaInput);
    } else if (e.key === 'Backspace' && !areaInput && areas.length > 0) {
      setAreas(prev => prev.slice(0, -1));
    }
  };

  const removeArea = (id: string) => setAreas(prev => prev.filter(a => a.id !== id));

  // ---------------------------------------------------------------------------
  // Generate
  // ---------------------------------------------------------------------------

  const handleGenerate = async () => {
    if (areas.length === 0) {
      setGenerationError('Add at least one service area to generate pages.');
      return;
    }
    setGenerating(true);
    setGenerationError('');
    setPreviews([]);
    setSimilarityReport([]);
    setWarnings([]);
    setCommitted(false);
    setCommitError('');

    try {
      const payload: GenerateLocationPagesPayload = {
        areas: areas.map(a => ({ area_name: a.area_name, region: a.region || undefined })),
        services_text: servicesText.trim() || undefined,
        include_testimonials: includeTestimonials,
        similarity_threshold: similarityThreshold,
      };
      const result = await generateLocationPages(siteId, payload);
      setPreviews(result.pages || []);
      setSimilarityReport(result.similarity_report || []);
      setWarnings(result.warnings || []);
      // Auto-expand first preview
      if (result.pages && result.pages.length > 0) {
        setExpandedPreviews(new Set([result.pages[0].page_id]));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed. Please try again.';
      setGenerationError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Commit
  // ---------------------------------------------------------------------------

  const handleCommit = async () => {
    if (previews.length === 0) return;
    setCommitting(true);
    setCommitError('');
    try {
      await commitLocationPages(siteId, {
        pages: previews,
        index_title: 'Areas We Serve',
        index_slug: 'areas-we-serve',
        add_nav_link: true,
      });
      setCommitted(true);
      onCommitted();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save pages. Please try again.';
      setCommitError(msg);
    } finally {
      setCommitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const togglePreview = (pageId: string) => {
    setExpandedPreviews(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const hasPreviews = previews.length > 0;
  const regeneratedPairs = similarityReport.filter(r => r.regenerated);
  const highSimilarityPairs = similarityReport.filter(r => r.similarity > similarityThreshold && !r.regenerated);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <GlassCard style={{
        width: '100%', maxWidth: 760, maxHeight: '92vh',
        overflowY: 'auto', padding: 0,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--stroke)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MapPin size={20} style={{ color: 'var(--brand-primary)' }} />
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)' }}>
              Areas We Serve
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', padding: 4, borderRadius: 4,
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {committed ? (
            /* Success state */
            <div style={{
              textAlign: 'center', padding: '40px 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            }}>
              <CheckCircle size={48} style={{ color: 'var(--brand-primary)' }} />
              <h3 style={{ margin: 0, color: 'var(--text)' }}>Pages Added to Site</h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)', maxWidth: 420, textAlign: 'center' }}>
                {previews.length} area page{previews.length !== 1 ? 's' : ''} plus an "Areas We Serve"
                index page have been added. Publish your site to make them live and register them in your sitemap.
              </p>
              <GlassButton variant="primary" onClick={onClose} style={{ marginTop: 8 }}>
                Done
              </GlassButton>
            </div>
          ) : (
            <>
              {/* Step 1 — Areas input */}
              <section>
                <label style={{
                  display: 'block', marginBottom: 8,
                  fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)',
                }}>
                  Service Areas
                  <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6, fontSize: '0.8125rem' }}>
                    (type an area name and press Enter — format: "City" or "City, State")
                  </span>
                </label>

                {/* Tag input */}
                <div
                  style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                    minHeight: 42, padding: '6px 10px',
                    border: '1px solid var(--stroke)', borderRadius: 8,
                    background: 'var(--panel)', cursor: 'text',
                  }}
                  onClick={() => inputRef.current?.focus()}
                >
                  {areas.map(a => (
                    <span
                      key={a.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: 'rgba(33,119,255,0.10)', color: 'var(--brand-primary)',
                        border: '1px solid rgba(33,119,255,0.25)',
                        borderRadius: 6, padding: '2px 8px',
                        fontSize: '0.8125rem', fontWeight: 500,
                      }}
                    >
                      {a.area_name}{a.region ? `, ${a.region}` : ''}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeArea(a.id); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--brand-primary)', padding: 0, lineHeight: 1,
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={inputRef}
                    value={areaInput}
                    onChange={e => setAreaInput(e.target.value)}
                    onKeyDown={handleAreaKeyDown}
                    onBlur={() => { if (areaInput.trim()) addArea(areaInput); }}
                    placeholder={areas.length === 0 ? 'e.g. Tulsa, OK' : ''}
                    style={{
                      flex: 1, minWidth: 120, border: 'none', outline: 'none',
                      background: 'transparent', fontSize: '0.875rem',
                      color: 'var(--text)', padding: '2px 0',
                    }}
                  />
                </div>

                {areas.length > 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {areas.length} area{areas.length !== 1 ? 's' : ''} — max 12 per generate call
                  </p>
                )}
              </section>

              {/* Step 2 — Services */}
              <section>
                <label style={{
                  display: 'block', marginBottom: 6,
                  fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)',
                }}>
                  Services to Feature
                  <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6, fontSize: '0.8125rem' }}>
                    (optional — comma-separated)
                  </span>
                </label>
                <textarea
                  value={servicesText}
                  onChange={e => setServicesText(e.target.value)}
                  placeholder="Roof repair, gutter cleaning, storm damage assessment..."
                  rows={2}
                  style={{
                    width: '100%', padding: '8px 12px', boxSizing: 'border-box',
                    border: '1px solid var(--stroke)', borderRadius: 8,
                    background: 'var(--panel)', color: 'var(--text)',
                    fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit',
                  }}
                />
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  If left blank, pages will draw on your brand mission and story. Specific services produce richer pages.
                </p>
              </section>

              {/* Options */}
              <section style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text)' }}>
                  <input
                    type="checkbox"
                    checked={includeTestimonials}
                    onChange={e => setIncludeTestimonials(e.target.checked)}
                    style={{ accentColor: 'var(--brand-primary)', cursor: 'pointer' }}
                  />
                  Include real testimonials
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: 'var(--text)' }}>
                  Similarity threshold:
                  <select
                    value={similarityThreshold}
                    onChange={e => setSimilarityThreshold(parseFloat(e.target.value))}
                    style={{
                      border: '1px solid var(--stroke)', borderRadius: 6, padding: '2px 6px',
                      background: 'var(--panel)', color: 'var(--text)', fontSize: '0.8125rem',
                    }}
                  >
                    <option value={0.35}>Strict (35%)</option>
                    <option value={0.45}>Default (45%)</option>
                    <option value={0.55}>Relaxed (55%)</option>
                  </select>
                </label>
              </section>

              {/* Generation error */}
              {generationError && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                  padding: '10px 14px', color: '#ef4444', fontSize: '0.875rem',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{generationError}</span>
                </div>
              )}

              {/* Generate button */}
              {!hasPreviews && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <GlassButton
                    variant="primary"
                    onClick={handleGenerate}
                    disabled={generating || areas.length === 0}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {generating ? (
                      <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                    ) : (
                      <><MapPin size={15} /> Generate Pages</>
                    )}
                  </GlassButton>
                  <GlassButton variant="secondary" onClick={onClose} disabled={generating}>
                    Cancel
                  </GlassButton>
                </div>
              )}

              {/* Generating notice */}
              {generating && (
                <div style={{
                  padding: '12px 16px', background: 'var(--panel-strong)',
                  border: '1px solid var(--stroke)', borderRadius: 8,
                  fontSize: '0.8125rem', color: 'var(--text-secondary)',
                }}>
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>
                    Generating {areas.length} area page{areas.length !== 1 ? 's' : ''}…
                  </p>
                  <p style={{ margin: '4px 0 0' }}>
                    This may take up to a few minutes. Gemini is crafting area-specific copy with local detail
                    and checking for similarity between pages. Please keep this window open.
                  </p>
                </div>
              )}

              {/* Previews */}
              {hasPreviews && (
                <>
                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <div style={{
                      background: 'var(--warning-bg, rgba(251,191,36,0.10))',
                      border: '1px solid var(--warning-border, rgba(251,191,36,0.40))',
                      borderRadius: 8, padding: '10px 14px',
                    }}>
                      <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '0.8125rem', color: 'var(--text)' }}>
                        Notices ({warnings.length})
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Similarity report */}
                  {similarityReport.length > 0 && (
                    <div style={{
                      border: '1px solid var(--stroke)', borderRadius: 8, overflow: 'hidden',
                    }}>
                      <div style={{
                        padding: '10px 14px', background: 'var(--panel-strong)',
                        borderBottom: '1px solid var(--stroke)',
                        fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        Anti-Doorway Similarity Report
                        <span style={{
                          background: 'rgba(33,119,255,0.10)',
                          color: 'var(--brand-primary)',
                          borderRadius: 4, padding: '1px 6px', fontSize: '0.75rem',
                        }}>
                          {regeneratedPairs.length > 0 ? `${regeneratedPairs.length} regenerated` : 'All unique'}
                        </span>
                      </div>
                      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {similarityReport.map((entry, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            fontSize: '0.8125rem', color: entry.similarity > similarityThreshold ? '#ef4444' : 'var(--text-secondary)',
                          }}>
                            <span>{entry.a} vs {entry.b}:</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                              {(entry.similarity * 100).toFixed(0)}% similar
                            </span>
                            {entry.regenerated && (
                              <span style={{
                                background: 'rgba(33,119,255,0.08)', color: 'var(--brand-primary)',
                                borderRadius: 4, padding: '0 5px', fontSize: '0.7rem',
                              }}>
                                regenerated
                              </span>
                            )}
                            {!entry.regenerated && entry.similarity <= similarityThreshold && (
                              <CheckCircle size={13} style={{ color: 'var(--brand-secondary, var(--brand-primary))' }} />
                            )}
                          </div>
                        ))}
                        {highSimilarityPairs.length > 0 && (
                          <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                            Some pairs remain above threshold. Consider regenerating or editing them manually.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Page previews */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text)' }}>
                      Generated Pages ({previews.length})
                    </h3>
                    {previews.map(page => {
                      const isExpanded = expandedPreviews.has(page.page_id);
                      return (
                        <div
                          key={page.page_id}
                          style={{
                            border: '1px solid var(--stroke)', borderRadius: 8, overflow: 'hidden',
                          }}
                        >
                          <button
                            onClick={() => togglePreview(page.page_id)}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '10px 14px', background: 'var(--panel-strong)', border: 'none',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <MapPin size={14} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)' }}>
                                {page.title}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                /{page.slug}
                              </span>
                            </div>
                            {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          </button>
                          {isExpanded && (
                            <div style={{ borderTop: '1px solid var(--stroke)' }}>
                              {page.preview_html ? (
                                <iframe
                                  srcDoc={page.preview_html}
                                  title={`Preview: ${page.title}`}
                                  style={{
                                    width: '100%', height: 480, border: 'none', display: 'block',
                                  }}
                                  sandbox="allow-same-origin"
                                />
                              ) : (
                                <div style={{
                                  padding: '20px', textAlign: 'center',
                                  color: 'var(--text-secondary)', fontSize: '0.8125rem',
                                }}>
                                  Preview not available for this page.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Commit error */}
                  {commitError && (
                    <div style={{
                      background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                      padding: '10px 14px', color: '#ef4444', fontSize: '0.875rem',
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                    }}>
                      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{commitError}</span>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <GlassButton
                      variant="primary"
                      onClick={handleCommit}
                      disabled={committing || previews.length === 0}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {committing ? (
                        <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                      ) : (
                        <><Plus size={15} /> Add to Site</>
                      )}
                    </GlassButton>
                    <GlassButton
                      variant="secondary"
                      onClick={handleGenerate}
                      disabled={generating || committing}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {generating ? (
                        <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Regenerating…</>
                      ) : (
                        'Regenerate'
                      )}
                    </GlassButton>
                    <GlassButton variant="secondary" onClick={onClose} disabled={committing}>
                      Cancel
                    </GlassButton>
                  </div>

                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    "Add to Site" appends the area pages and an "Areas We Serve" index page to your
                    site document. Publish the site afterward to make them live and register them in
                    your sitemap.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </GlassCard>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default LocationPagesPanel;
